import { ReactElement, useEffect, useState } from 'react'
import { useAuth } from 'context/AuthContext'
import {
    putApiRequest,
    postApiRequest,
    useFetch,
    deleteApiRequest,
} from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { classNames } from '@progress/kendo-react-common'
import DataTable from '../../DataTable'
import { SpaceForm, Spaces } from '../../../types/spaces'
import AddSpace from 'components/AlertPrompt/AddSpace'
import ConfirmDialogModal from '../ConfirmDialog'
import Translation from '../../Translation'
import ErrorModal from '../ErrorModal'
import { useAppContext } from '../../../context/AppContext'
import { useTenantContext } from 'context/TenantContext'
import { GlobalDialogType } from '../GlobalDialog'
import getConfig from 'next/config'

function LocationSpaces() {
    const ctx = useAuth()
    const { translations } = useAppContext()
    const { getLocations } = useTenantContext()
    const { attributes, setAttributes } = ctx.LocationModal
    const [spaces, setSpaces] = useState<Spaces[]>([])
    const [add_space, toggleAddSpaceWindow] = useState<boolean>(false)
    const [to_archive_space, setToArchive] = useState<SpaceForm>()
    const [prompt_archive_message, toggleArchiveModal] = useState<
        string | ReactElement
    >('')
    const [prompt_message, toggleDialog] = useState<string>('')
    const [space_form, setSpaceForm] = useState<SpaceForm>({
        location_id: attributes?.id as string,
        max_capacity: 0,
        name: '',
    })
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig


    const {
        data: location_spaces,
        is_loading: is_fetching_spaces,
        doFetch,
    } = useFetch(
        `/v1/location-spaces/location-id?locationId=${attributes?.id}`,
        FetchMethods.GET
    )

    const editSpaceHandler = async (space: SpaceForm) => {
        const {
            id,
            location_id,
            name,
            max_capacity,
            notes,
            image_url,
            reuse,
        } = space

        const payload = {
            id,
            locationId: location_id,
            maxCapacity: max_capacity,
            name,
            notes,
            imageUrl: image_url,
            reuse,
        }

        try {
            const res = id
                ? await putApiRequest(`/v1/location-spaces/${id}`, payload)
                : await postApiRequest(`/v1/location-spaces`, payload)
            if (res?.ok) {
                getLocations()
                ctx.GlobalDialog.setAttributes({
                    title: !id
                        ? 'locations.success_add_space'
                        : 'locations.update_add_space',
                    button: 'locations.back_to_spaces',
                    message: name,
                    className: 'w-1/3 overflow-hidden',
                    type: GlobalDialogType.SUCCESS,
                })
                ctx.GlobalDialog.open()
            }
        } catch (err) {
            //TODO error handling
        }
        toggleAddSpaceWindow(false)
        doFetch()
    }

    useEffect(() => {
        if (location_spaces.content && location_spaces.content?.length) {
            const spaces = location_spaces.content.map(
                (i: Record<string, string>) => {
                    const {
                        id,
                        locationId,
                        maxCapacity,
                        name,
                        imageUrl,
                        notes,
                        reuse,
                    } = i
                    return {
                        id,
                        location_id: locationId as string,
                        max_capacity: maxCapacity,
                        name,
                        image_url: imageUrl ?? '',
                        notes,
                        reuse,
                    }
                }
            )
            setSpaces(spaces)
        }
    }, [location_spaces])

    useEffect(() => {
        if (!add_space) {
            setSpaceForm({
                location_id: attributes?.id as string,
                max_capacity: 0,
                name: '',
            })
        }
    }, [add_space])

    async function handleArchiveConfirm() {
        const api = await deleteApiRequest(
            `/v1/location-spaces/${to_archive_space?.id}`
        )
        if (api?.ok) {
            setToArchive(undefined)
            toggleArchiveModal('')
            ctx.GlobalDialog.setAttributes({
                title: 'locations.deleted_space',
                button: 'locations.back_to_spaces',
                message: to_archive_space?.name as string,
                className: 'w-1/3 overflow-hidden',
                type: GlobalDialogType.SUCCESS,
            })
            ctx.GlobalDialog.open()
            doFetch()
        } else {
            toggleDialog(api.message)
        }
    }

    function RenderSpaces(props: any) {
        const onArchive = (s: SpaceForm, space_idx: number) => {
            toggleArchiveModal(
                <Translation
                    content_key="archive_service"
                    content_values={{
                        name: s.name,
                    }}
                />
            )
            setToArchive(s)
        }
        const spaces: Spaces[] = props.spaces
        const rows: HTMLTableRowElement[] = spaces.map(
            (
                {
                    id,
                    name,
                    location_id,
                    max_capacity,
                    notes,
                    image_url,
                    reuse,
                }: Spaces,
                idx
            ) =>
                (
                    <tr
                        key={id}
                        className={classNames(
                            !(idx % 2)
                                ? 'bg-primary-lighter bg-opacity-30'
                                : '',
                            'hover:bg-secondary hover:bg-opacity-10'
                        )}
                    >
                        <td className="px-6 py-4 align-top w-1/2">
                            <div className="flex flex-row gap-5">
                                <div>
                                    <img
                                        src={
                                            image_url != ''
                                                ? image_url
                                                : `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/location/location_fallback.jpg`
                                        }
                                        className="object-fill h-12 w-12 rounded-lg"
                                        alt="spaces"
                                    />
                                </div>
                                <div className=" flex flex-col">
                                    <div className="block font-bold">
                                        {name}
                                    </div>
                                    <div className="block text-xs text-gray-500">
                                        {notes}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                            <Translation
                                render_as="span"
                                content_key="space_max_capacity"
                                content_values={{
                                    max: max_capacity as unknown as string,
                                }}
                            />
                        </td>
                        <td className="px-6 py-4 align-top flex justify-end gap-3">
                            <button
                                type="button"
                                className=" bg-opacity-50 text-sm text-primary-light rounded-lg "
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const space_row: SpaceForm = {
                                        id: id as string,
                                        location_id: location_id as string,
                                        max_capacity:
                                            max_capacity as number,
                                        name: name as string,
                                        notes: notes as string,
                                        image_url: image_url as string,
                                        reuse: !!reuse ? reuse : false,
                                    }

                                    setSpaceForm(space_row)
                                    toggleAddSpaceWindow(true)
                                }}
                            >
                                <i className="feather-edit" />
                                {/* <Translation content_key="Edit" /> */}
                            </button>
                            <button
                                type="button"
                                className=" text-red-500  text-sm rounded-lg"
                                onClick={async (e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const space_row: SpaceForm = {
                                        id: id as string,
                                        location_id: location_id as string,
                                        max_capacity:
                                            max_capacity as number,
                                        name: name as string,
                                        notes: notes as string,
                                        image_url: image_url as string,
                                    }
                                    onArchive(space_row, idx)
                                }}
                            >
                                <i className="feather-archive" />
                            </button>
                        </td>
                    </tr>
                ) as unknown as HTMLTableRowElement
        )

        return (
            <div className="flex flex-col">
                <div className="align-middle inline-block min-w-full">
                    <div className="flex justify-end">
                        <button
                            className="relative py-1 px-2 border border-transparent rounded-md text-white focus:outline-none bg-primary"
                            onClick={() => {
                                toggleAddSpaceWindow(true)
                            }}
                        >
                            Add Space
                        </button>
                    </div>
                    <div className="mt-4">
                        <DataTable
                            rows={rows}
                            columns={[
                                {
                                    label:
                                        translations['space_name'] ||
                                        'Space Name',
                                    classNames:
                                        'px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                                },
                                {
                                    label:
                                        translations[
                                            'space_maximum_capacity'
                                        ] || 'Maximum Capacity',
                                    classNames:
                                        'px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                                },
                                {
                                    label: '',
                                    classNames:
                                        'px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                                },
                            ]}
                        />
                    </div>
                </div>
                <div className="flex-1">
                    {add_space && (
                        <AddSpace
                            openModal={add_space}
                            setOpenModal={toggleAddSpaceWindow}
                            space_data={space_form}
                            onSaveHandler={editSpaceHandler}
                        />
                    )}
                </div>
            </div>
        )
    }

    return (
        <section className="px-2">
            <div className="z-50">
                <RenderSpaces
                    spaces={spaces}
                    // handleEdit={handleEdit}
                    // handleCancel={handleCancel}
                />
                <ConfirmDialogModal
                    prompt_message={prompt_archive_message}
                    onConfirm={handleArchiveConfirm}
                    onCancel={() => {
                        toggleArchiveModal('')
                    }}
                />
                <>
                    {prompt_message.length > 0 && (
                        <ErrorModal
                            error_message={prompt_message}
                            onCloseModal={() => {
                                toggleDialog('')
                            }}
                            type="success"
                        />
                    )}
                </>
            </div>
        </section>
    )
}

export default LocationSpaces
